import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { FileSearchOutlined, RightOutlined } from '@ant-design/icons';
import {
  format,
  parseISO,
  isWithinInterval,
  differenceInMilliseconds,
} from 'date-fns';

// COMPONENTS
import ToastErrorAlert from 'components/ToastAlert/ToastErrorAlert';

// DS
import {
  VertContainer,
  Col,
  Row,
  VertTypography,
  VertSelect,
  VertDatePicker,
  VertIcon,
  VertButton,
  Table,
  VertLoading,
  VertBreadcrumb,
} from '@vertem-core/vertice-ds';

// SERVICES
import {
  FetchExtractSummary,
  FetchExtractByFilters,
  FetchExtractToExpire,
} from 'Services/Apis/Extract/extractServices';

// STYLES
import 'Assets/styles/default/pages/extract/ExtractStyles.less';

// UTILS
import { formatDayMonthYear } from 'Utils/commons/date';
import { pointsMask } from 'Utils/commons/points';
import generateUUID from 'Utils/generateUUID';

// EXTRACT PAGE
const ExtractPage = () => {
  /* Hooks */
  const navigate = useNavigate();
  const Balance = useSelector(({ Balance: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  const userInfos = useSelector(({ oidc: data }) => data);

  /* States */
  const [isLoading, setIsLoading] = useState(false);
  const [selectFieldValue, setSelectFieldValue] = useState('');
  const [extractSummary, setExtractSummary] = useState({});
  const [extractList, setExtractList] = useState([]);
  const [hasListError, setHasListError] = useState(false);
  const [hasSummaryError, setHasSummaryError] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({
    startDate: '',
    endDate: '',
  });
  // TODO: Verificar quando podemos utilizar apos BFF tratar a API.
  // const [selectedDate, setSelectedDate] = useState('');

  /* Vars */
  const isEmpty = extractList.length < 1;
  const availablePoints =
    Balance && Balance.pointsValue ? Balance.pointsValue : 0;
  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const summaryTotalPoints = availablePoints + extractSummary?.lockedBalance || 0;
  const formattedAvailablePoints = pointsMask(
    availablePoints,
    decimalPlaces,
    pointsName
  );
  const formattedProcessingPoints = pointsMask(
    extractSummary?.lockedBalance,
    decimalPlaces,
    pointsName
  );
  const formattedExpiredPoints = pointsMask(
    extractSummary?.expiredBalance,
    decimalPlaces,
    pointsName
  );
  const formattedReemdedPoints = pointsMask(
    extractSummary?.redeemBalance,
    decimalPlaces,
    pointsName
  );
  const formattedCreditedPoints = pointsMask(
    extractSummary?.creditBalance,
    decimalPlaces,
    pointsName
  );
  const formattedTotalPoints = pointsMask(
    summaryTotalPoints,
    decimalPlaces,
    pointsName
  );
  const ExtractListErrorMessage = {
    message: 'Erro na listagem de extrato',
  };
  const ExtractSummaryErrorMessage = {
    message: 'Erro ao carregar dados do extrato',
  };
  const breadcrumb = [
    {
      title: 'Início',
      path: '/',
    },
    {
      title: 'Extrato',
      path: '/extract',
    },
  ];
  const periodOptions = [
    { value: '1', label: 'Último mês', availability: true },
    { value: '2', label: 'Últimos 2 meses', availability: true },
    { value: '3', label: 'Últimos 3 meses', availability: true },
    { value: '6', label: 'Últimos 6 meses', availability: true },
    { value: '12', label: 'Últimos 12 meses', availability: true },
    { value: '24', label: 'Últimos 24 meses', availability: true }
  ];
  const EHistoryType = {
    EXPIRED: 'EXPIRED',
    CREDIT: 'CREDIT',
    DEBIT: 'DEBIT',
  };

  const statusLabel = {
    [EHistoryType.CREDIT]: 'Expira em',
    [EHistoryType.DEBIT]: 'Resgatado em',
    [EHistoryType.EXPIRED]: 'Expirado em',
  };

  const columns = [
    {
      title: 'Data',
      dataIndex: 'date',
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
    },
    {
      title: 'Quantidade de pontos',
      dataIndex: 'points_quantity',
    },
    {
      title: 'Status',
      dataIndex: 'status',
    },
  ];

  /* Handlers */
  const selectPeriodFilterHandler = (selectedRangePeriod) => {
    const { startDate, endDate } = calculateDate(selectedRangePeriod);
    setSelectFieldValue(selectedRangePeriod);
    getExtractList(startDate, endDate);
  };

  const goToProductsHandler = () => {
    navigate('/search/1/filter/JTdCJTIyc29ydCUyMiUzQSUyMlNDT1JFJTIyJTdE');
  };

  // TODO: Verificar quando podemos utilizar apos BFF tratar a API.
  // const selectDataFilterHandler = (date) => {
  //   if (!date) {
  //     return;
  //   }

  //   setIsLoading(true);

  //   const formattedDate  = format(date.$d, 'yyyy-MM-dd');

  //   setTimeout(() => {
  //     setSelectedPeriod({
  //       startDate: '',
  //       endDate: '',
  //     });
  //     setSelectFieldValue('');
  //     setSelectedDate(formattedDate);
  //     setIsLoading(false);
  //   }, 3000);
  // };

  /* Utils */
  const calculateDate = (selectedRangePeriod) => {
    const convertedRangeSelected = Number(selectedRangePeriod);
    const dataObj = new Date(currentDate);
    const formattedCurrentDate = dataObj.toISOString().split('T')[0];

    const calculatedDate = new Date(dataObj);
    calculatedDate.setMonth(dataObj.getMonth() - convertedRangeSelected);

    const formattedStartDate = calculatedDate.toISOString().split('T')[0];
    const formattedEndDate = formattedCurrentDate;

    return {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  };

  const getExtractSummary = async () => {
    try {
      setIsLoading(true);
      setHasSummaryError(false);
      const response = await FetchExtractSummary(userInfos.user);
      setExtractSummary(response);
    } catch (error) {
      console.info('getExtractSummary Error => ', error);
      setHasSummaryError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getExtractList = async (startDate, endDate) => {
    try {
      setIsLoading(true);
      setHasListError(false);

      // chamadas das API`s de extrato
      const extractListData = await FetchExtractByFilters(
        userInfos.user,
        startDate,
        endDate
      );
      const extractListToExpireData = await FetchExtractToExpire(
        userInfos.user
      );

      // Trata e filtra os dados vindos da api de pontos a expirar de acordo com o startDate e endDate;
      const startDateParsed = parseISO(startDate);
      const endDateParsed = parseISO(endDate);
      const filteredExtractToExpire = extractListToExpireData
        .map((item) => {
          const expirationDate = parseISO(item.expirationDate);
          const isInInterval = isWithinInterval(expirationDate, {
            start: startDateParsed,
            end: endDateParsed,
          });
          return {
            ...item,
            date: item.expirationDate,
            valuePoints: item.value,
            type: isInInterval ? EHistoryType.EXPIRED : undefined,
          };
        })
        .filter((item) => item.type === EHistoryType.EXPIRED);

      // junta os dois arrays
      const combinedArray = extractListData.concat(filteredExtractToExpire);

      // ordenar o array pela data de forma inversa colocando os dados mais recente em primeiro lugar
      const sortedData = combinedArray.sort((a, b) =>
        differenceInMilliseconds(parseISO(b.date), parseISO(a.date))
      );

      // salva os dados nos states
      setExtractList(sortedData);
      setSelectedPeriod({
        startDate,
        endDate,
      });

      // TODO: Verificar quando podemos utilizar apos BFF tratar a API.
      // setSelectedDate('');
    } catch (error) {
      console.info('getExtractList Error => ', error);
      setHasListError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getExtractLabel = (type) => {
    const extractLabels = {
      [EHistoryType.CREDIT]: 'pontos creditados',
      [EHistoryType.DEBIT]: 'pontos resgatados',
      [EHistoryType.EXPIRED]: 'pontos expirados',
    };

    return extractLabels[type] || 'pontos';
  };

  const normalizeTableData = (items) => {
    if (items) {
      

      const normalizedItems = items.map((item) => {
        const formattedValue = pointsMask(
          item.valuePoints,
          decimalPlaces,
          pointsName
        );
        return {
          key: generateUUID(),
          date: formatDayMonthYear(item.date),
          description: item.description,
          points_quantity: `${formattedValue.value} ${formattedValue.label}`,
          status:
            item.type === EHistoryType.DEBIT
              ? `${statusLabel[item.type]}: ${formatDayMonthYear(item.date)}`
              : `${statusLabel[item.type]}: ${formatDayMonthYear(
                item.expirationDate
              )}`,
        };
      });

      return normalizedItems;
    }

    return [];
  };

  /* Lifecycles */
  useEffect(() => {
    const { startDate, endDate } = calculateDate(1);
    getExtractList(startDate, endDate);
    getExtractSummary();
  }, []);

  /* Renders */
  return (
    <VertContainer
      spacingX="regular"
      className="extract-page-container padding-top-small padding-bottom-regular"
    >
      <Col className="extract-page-content col col-auto">
        {/* Alert Error */}
        {!isLoading && hasListError && (
          <ToastErrorAlert
            className="margin-bottom-small"
            error={ExtractListErrorMessage}
          />
        )}
        {!isLoading && hasSummaryError && (
          <ToastErrorAlert
            className="margin-bottom-small"
            error={ExtractSummaryErrorMessage}
          />
        )}

        {/* Breadcrumb */}
        <Row className="extract-page-breadcrumb" align="middle" gutter={6}>
          <VertBreadcrumb hasBackButton={true}
            separator={<VertIcon component={RightOutlined} />}
            items={breadcrumb}
          />
        </Row>

        {/* DESKTOP */}
        <MediaQuery minWidth={767}>
          <Row className="extract-page-title" align="middle" gutter={6}>
            <VertTypography.Title>Extrato de pontos</VertTypography.Title>
          </Row>

          <Row className="extract-page-info" align="middle" gutter={6}>
            <Col className="col col-3">
              <VertTypography.Paragraph>Disponível</VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedAvailablePoints.value}{' '}
                {formattedAvailablePoints.label}
              </VertTypography.Paragraph>
            </Col>
            <Col className="col col-3">
              <VertTypography.Paragraph>
                Em processamento
              </VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedProcessingPoints.value}{' '}
                {formattedProcessingPoints.label}
              </VertTypography.Paragraph>
            </Col>
            <Col className="col col-3">
              <VertTypography.Paragraph>Total</VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedTotalPoints.value}{' '}
                {formattedTotalPoints.label}
              </VertTypography.Paragraph>
            </Col>
          </Row>

          <Col className="divider" />

          <Row className="extract-page-info" align="middle" gutter={6}>
            <Col className="col col-3">
              <VertTypography.Paragraph>
                Pontos expirados
              </VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedExpiredPoints.value} {formattedExpiredPoints.label}
              </VertTypography.Paragraph>
            </Col>
            <Col className="col col-3">
              <VertTypography.Paragraph>
                Pontos regatados
              </VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedReemdedPoints.value} {formattedReemdedPoints.label}
              </VertTypography.Paragraph>
            </Col>
            <Col className="col col-3">
              <VertTypography.Paragraph>
                Pontos creditados
              </VertTypography.Paragraph>
              <VertTypography.Paragraph strong>
                {formattedCreditedPoints.value} {formattedCreditedPoints.label}
              </VertTypography.Paragraph>
            </Col>
          </Row>

          <Row
            className="extract-page-filters"
            align="middle"
            justify="space-between"
            gutter={6}
          >
            <Col className="filter-by-period col col-3">
              <VertTypography.Paragraph>
                Filtrar por período
              </VertTypography.Paragraph>
              <VertSelect
                items={periodOptions}
                placeholder="Selecionar"
                template="marketplace"
                onChange={(value) => selectPeriodFilterHandler(value)}
                value={selectFieldValue}
              />
            </Col>
            {/* TODO: Verificar quando podemos utilizar apos BFF tratar a API. */}
            {/* <Col className='filter-by-date col col-3'>
              <VertTypography.Paragraph>Filtrar por data</VertTypography.Paragraph>
              <VertDatePicker
                popupClassName='extract-page-datepicker'
                format='DD/MM/YYYY'
                onChange={(date) => selectDataFilterHandler(date)}
                value={selectedDate}
              />
            </Col> */}
          </Row>

          {isLoading && (
            <Row
              className="extract-page-loading"
              align="middle"
              justify="center"
              gutter={6}
            >
              <VertLoading type="spin" />
            </Row>
          )}

          {isEmpty && !isLoading && !hasListError && (
            <Row
              className="extract-page-empty"
              align="middle"
              justify="center"
              gutter={6}
            >
              <Col className="col">
                <VertIcon component={FileSearchOutlined} />
                <VertTypography.Title>
                  Você ainda não possui um extrato de pontos
                </VertTypography.Title>
                <VertTypography.Paragraph className="empty-info">
                  Entre as datas {formatDayMonthYear(selectedPeriod.startDate)}{' '}
                  e {formatDayMonthYear(selectedPeriod.endDate)}
                </VertTypography.Paragraph>
                <VertTypography.Paragraph>
                  Aproveite nossos produtos e serviços e resgate agora mesmo!
                </VertTypography.Paragraph>
                <VertButton type="highlight" onClick={goToProductsHandler}>
                  Ver produtos
                </VertButton>
              </Col>
            </Row>
          )}

          {!isLoading && !isEmpty && (
            <Row
              className="extract-page-history"
              align="middle"
              justify="center"
              gutter={6}
            >
              <Col className="col">
                <Table
                  rootClassName="extract-page-history-table"
                  pagination={false}
                  bordered
                  columns={columns}
                  dataSource={normalizeTableData(extractList)}
                />
              </Col>
            </Row>
          )}
        </MediaQuery>

        {/* MOBILE */}
        <MediaQuery maxWidth={767}>
          <Row className="extract-page-title" align="middle" gutter={6}>
            <VertTypography.Title>Extrato de pontos</VertTypography.Title>
          </Row>

          <Row className="extract-page-info" align="middle" gutter={6}>
            <Col className="col info-column-1">
              <Col className="col info">
                <VertTypography.Paragraph>Disponível</VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedAvailablePoints.value}{' '}
                  {formattedAvailablePoints.label}
                </VertTypography.Paragraph>
              </Col>
              <Col className="col info">
                <VertTypography.Paragraph>
                  Em processamento
                </VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedProcessingPoints.value}{' '}
                  {formattedProcessingPoints.label}
                </VertTypography.Paragraph>
              </Col>
              <Col className="col info">
                <VertTypography.Paragraph>Total</VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedTotalPoints.value}{' '}
                  {formattedTotalPoints.label}
                </VertTypography.Paragraph>
              </Col>
            </Col>
            <Col className="col info-column-2">
              <Col className="col info">
                <VertTypography.Paragraph>
                  Pontos expirados
                </VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedExpiredPoints.value} {formattedExpiredPoints.label}
                </VertTypography.Paragraph>
              </Col>
              <Col className="col info">
                <VertTypography.Paragraph>
                  Pontos regatados
                </VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedReemdedPoints.value} {formattedReemdedPoints.label}
                </VertTypography.Paragraph>
              </Col>
              <Col className="col info">
                <VertTypography.Paragraph>
                  Pontos creditados
                </VertTypography.Paragraph>
                <VertTypography.Paragraph strong>
                  {formattedCreditedPoints.value}{' '}
                  {formattedCreditedPoints.label}
                </VertTypography.Paragraph>
              </Col>
            </Col>
          </Row>

          <Col className="divider" />

          <Row
            className="extract-page-filters"
            align="middle"
            justify="center"
            gutter={6}
          >
            <Col className="col">
              <Col className="filter-by-period col">
                <VertTypography.Paragraph>
                  Filtrar por período
                </VertTypography.Paragraph>
                <VertSelect
                  items={periodOptions}
                  placeholder="Selecionar"
                  template="marketplace"
                  onChange={(value) => selectPeriodFilterHandler(value)}
                  value={selectFieldValue}
                />
              </Col>
              {/* TODO: Verificar quando podemos utilizar apos BFF tratar a API. */}
              {/* <Col className='filter-by-date col'>
                <VertTypography.Paragraph>Filtrar por data</VertTypography.Paragraph>
                <VertDatePicker
                  popupClassName='extract-page-datepicker'
                  format='DD/MM/YYYY'
                  onChange={(date) => selectDataFilterHandler(date)}
                  value={selectedDate}
                />
              </Col> */}
            </Col>
          </Row>

          {isLoading && (
            <Row
              className="extract-page-loading"
              align="middle"
              justify="center"
              gutter={6}
            >
              <VertLoading type="spin" size="small" />
            </Row>
          )}

          {isEmpty && !isLoading && !hasListError && (
            <Row
              className="extract-page-empty"
              align="middle"
              justify="center"
              gutter={6}
            >
              <Col className="col">
                <VertIcon component={FileSearchOutlined} />
                <VertTypography.Title>
                  Você ainda não possui um extrato de pontos
                </VertTypography.Title>
                <VertTypography.Paragraph className="empty-info">
                  Entre as datas {formatDayMonthYear(selectedPeriod.startDate)}{' '}
                  e {formatDayMonthYear(selectedPeriod.endDate)}
                </VertTypography.Paragraph>
                <VertTypography.Paragraph>
                  Aproveite nossos produtos e serviços e resgate agora mesmo!
                </VertTypography.Paragraph>
                <VertButton type="highlight" onClick={goToProductsHandler}>
                  Ver produtos
                </VertButton>
              </Col>
            </Row>
          )}

          {!isLoading && !isEmpty && (
            <Row
              className="extract-page-history"
              align="middle"
              justify="center"
              gutter={6}
            >
              <Col className="col">
                {extractList.map((item) => {
                  return (
                    <Col className="col history-item" key={generateUUID()}>
                      <VertTypography.Paragraph className="date-info">
                        Data {formatDayMonthYear(item.date)}
                      </VertTypography.Paragraph>
                      <VertTypography.Paragraph className="points-info">
                        <b>
                          {
                            pointsMask(
                              item.valuePoints,
                              decimalPlaces,
                              pointsName
                            ).value
                          }
                        </b>{' '}
                        {getExtractLabel(item.type)}
                      </VertTypography.Paragraph>
                      <VertTypography.Paragraph className="points-info">
                        <b>Descrição:</b>{' '}
                        {item.description}
                      </VertTypography.Paragraph>
                      <VertTypography.Paragraph className="points-info">
                        <b>{statusLabel[item.type]}:</b>{' '}
                        {formatDayMonthYear( item.type === EHistoryType.DEBIT ? item.date : item.expirationDate)}
                      </VertTypography.Paragraph> 
                    </Col>
                  );
                })}
              </Col>
            </Row>
          )}
        </MediaQuery>
      </Col>
    </VertContainer>
  );
};

export default ExtractPage;
